import API_CONFIG from '../config/api';

export interface StreamUpdate {
  current_step: number;
  current_step_description: string;
  total_steps: number;
  success: boolean;
  qrcode_base64_image: string | null;
  legal_representative_identification_document_already_sent: boolean | null;
}

type StreamHandler = (update: Partial<StreamUpdate>) => void;

interface OpenStreamResult {
  controller: AbortController;
  promise: Promise<void>;
}

export interface IdentificationDocumentPayload {
  file_name: string;
  file_extension: string;
  file_base64: string;
}

const buildAuthHeaders = (token?: string): Record<string, string> => {
  const resolvedToken = token ?? localStorage.getItem('token') ?? undefined;
  const headers: Record<string, string> = {
    Accept: 'text/event-stream, application/x-ndjson, application/json',
    'Cache-Control': 'no-cache',
  };
  if (resolvedToken) {
    headers.Authorization = `Bearer ${resolvedToken}`;
  }
  return headers;
};

const readStream = async (response: Response, onUpdate: StreamHandler, signal: AbortSignal) => {
  if (!response.body) {
    throw new Error('Resposta sem corpo (stream).');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  let streamFinished = false;

  while (!streamFinished) {
    const { value, done } = await reader.read();
    if (done) {
      streamFinished = true;
      continue;
    }
    if (!value) {
      continue;
    }
    // Abort cooperativo
    if (signal.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    buffer += decoder.decode(value, { stream: true });

    const parts = buffer.split(/\r?\n/);
    buffer = parts.pop() || '';

    for (const rawLine of parts) {
      const line = rawLine.trim();
      if (!line) continue;

      const jsonCandidate = line.startsWith('data:') ? line.replace(/^data:\s*/, '') : line;
      try {
        const obj = JSON.parse(jsonCandidate) as Partial<StreamUpdate>;
        onUpdate(obj);
      } catch {
        // Ignorar linhas não-JSON
      }
    }
  }
};

export const openFinishFormalizationStream = (
  searchTerm: string,
  onUpdate: StreamHandler,
  token?: string
): OpenStreamResult => {
  const controller = new AbortController();
  const headers = buildAuthHeaders(token);
  const url = `${API_CONFIG.BASE_URL_WITH_VERSION}/credit-analysis/${searchTerm}/finish-formalization`;

  const promise = fetch(url, {
    method: 'GET',
    headers,
    signal: controller.signal,
  }).then(async response => {
    await readStream(response, onUpdate, controller.signal);
  });

  return { controller, promise };
};

export const openUploadIdentificationDocumentStream = (
  searchTerm: string,
  payload: IdentificationDocumentPayload,
  onUpdate: StreamHandler,
  token?: string
): OpenStreamResult => {
  const controller = new AbortController();
  const headers = {
    ...buildAuthHeaders(token),
    'Content-Type': 'application/json',
  };
  const url = `${API_CONFIG.BASE_URL_WITH_VERSION}/credit-analysis/${searchTerm}/finish-formalization/send-identification-document`;

  const promise = fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
    signal: controller.signal,
  }).then(async response => {
    await readStream(response, onUpdate, controller.signal);
  });

  return { controller, promise };
};
